/**
 * EVERYTHING BEHIND SIGN-IN.
 *
 * The session is resolved once here and handed to every child route's context,
 * so each guard (`requirePerm`, `requireFeature`, `requireModule`) reads it
 * without a second request. The reader's layout is painted from the session
 * before the first frame.
 */
import { Outlet, createFileRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { Suspense } from "react";
import { AppHeader } from "@/components/layout/app-header";
import { useT } from "@/i18n";
import { sessionQuery, type Session } from "@/lib/auth/session";
import { useSearchHotkey, useSearchOpen } from "@/lib/search";
import { ViewProvider } from "@/lib/view-provider";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.query(sessionQuery);
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session };
  },
  component: AppLayout,
});

/**
 * THE PALETTE IS FETCHED WHEN IT IS ASKED FOR, as the `?` sheet is
 * (`routes/__root.tsx`): the boolean is eager, the dialogue is not.
 */
const LazySearchPalette = lazyRouteComponent(
  () => import("@/components/layout/search-palette"),
  "SearchPalette",
);

function SearchPaletteHost({ session }: { session: Session }) {
  useSearchHotkey();
  const open = useSearchOpen();
  if (!open) return null;
  return (
    <Suspense>
      <LazySearchPalette session={session} />
    </Suspense>
  );
}

function AppLayout() {
  /**
   * THE ROUTE CONTEXT, AND NOT `useSession()`, WHICH COSTS 4.3 kB.
   *
   * The context is a snapshot `beforeLoad` resolved, so it does not change
   * again until the router re-runs. That was invisible while nothing on a
   * signed-in screen could change the session, and then the layout picker
   * arrived (21-09-2026): pressing *Windows grid view* wrote the store, wrote
   * the query cache, and repainted nothing, because this is what
   * `ViewProvider` reads.
   *
   * The obvious fix is `useSession()`. It was measured and put back:
   * `useQuery` is not otherwise on the first-paint path — `beforeLoad` uses
   * `queryClient.query`, which is the core and not the React binding — so
   * calling it HERE drags `useQuery`, `queryObserver`, `query` and
   * `infiniteQueryBehavior` into the eager chunk. 15 kB raw, 4.3 kB gzipped,
   * and it took the bundle over its ceiling.
   *
   * So the header calls `router.invalidate()` after it writes, `beforeLoad`
   * re-runs, and the cache it reads is the one that was just set. The cost of
   * that is re-running the loaders on a press that happens a few times a
   * week; the cost of the other is 4.3 kB on every first paint in the estate.
   */
  const { session } = Route.useRouteContext();
  const { t } = useT();
  return (
    <ViewProvider view={session.view}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-s2 focus:left-s2 focus:rounded-md focus:bg-surface focus:p-s2"
      >
        {t("app.skipToContent")}
      </a>
      <div className="flex h-dvh flex-col">
        <AppHeader session={session} />
        <main id="main" className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <SearchPaletteHost session={session} />
    </ViewProvider>
  );
}
